import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, MapPin, Loader2, ShieldCheck } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_META, STATUS_META } from "@/lib/communityMeta";

export default function AdminIssues() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [issues, setIssues] = useState([]);
  const [reps, setReps] = useState([]);
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState({ is_admin: false });

  const loadRequests = () => api.get("/admin/resolver-requests", { params: { status: "pending" } })
    .then((r) => setRequests(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/me/community-flags").then((r) => setFlags(r.data));
  }, []);

  useEffect(() => {
    if (!flags.is_admin) return;
    setLoading(true);
    Promise.all([
      api.get("/admin/issues/stats"),
      api.get("/issues", { params: { status: status !== "all" ? status : undefined } }),
      api.get("/admin/users/reps").catch(() => ({ data: [] })),
      api.get("/admin/resolver-requests", { params: { status: "pending" } }).catch(() => ({ data: [] })),
    ]).then(([a, b, c, d]) => { setStats(a.data); setIssues(b.data); setReps(c.data); setRequests(d.data); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [status, flags.is_admin]);

  const decideRequest = async (reqId, decision) => {
    try { await api.post(`/admin/resolver-requests/${reqId}/decide`, { decision });
      toast.success(`Request ${decision}d`); await loadRequests();
      const r2 = await api.get("/admin/users/reps"); setReps(r2.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!user) return null;
  if (!flags.is_admin) {
    return (
      <AppShell>
        <div className="px-4 py-24 text-center">
          <ShieldCheck className="h-10 w-10 text-gray-400 mx-auto" />
          <h1 className="text-2xl font-bold mt-3">Admin only</h1>
          <p className="text-gray-600 mt-2">You do not have access to this page.</p>
        </div>
      </AppShell>
    );
  }

  const assignRep = async (issueId, repId) => {
    try { await api.post(`/issues/${issueId}/assign`, { rep_user_id: repId }); toast.success("Representative assigned");
      const r = await api.get("/issues", { params: { status: status !== "all" ? status : undefined } }); setIssues(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changeStatus = async (issueId, newStatus) => {
    try { await api.post(`/issues/${issueId}/status`, { status: newStatus }); toast.success("Status updated");
      const [sa, sb] = await Promise.all([api.get("/admin/issues/stats"), api.get("/issues", { params: { status: status !== "all" ? status : undefined } })]);
      setStats(sa.data); setIssues(sb.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Admin</div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Community Issues Management</h1>

        {stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              ["Total", stats.total, "bg-gray-900 text-white"],
              ["Reported", stats.reported, STATUS_META.reported.color],
              ["Under Review", stats.under_review, STATUS_META.under_review.color],
              ["In Progress", stats.in_progress, STATUS_META.in_progress.color],
              ["Resolved", stats.resolved, STATUS_META.resolved.color],
              ["Unassigned", stats.unassigned, "bg-red-50 text-red-800 border border-red-200"],
            ].map(([label, val, cls]) => (
              <div key={label} className={`rounded-2xl p-4 ${cls}`}>
                <div className="text-[10px] uppercase tracking-widest opacity-75">{label}</div>
                <div className="text-2xl font-bold mt-1">{val}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex gap-2 flex-wrap">
          {["all", "reported", "under_review", "awaiting_assignment", "assigned", "in_progress", "resolution_submitted", "unable_to_resolve", "resolved", "closed_not_resolved"].map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-4 py-2 rounded-full text-xs font-semibold ${status === s ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-700"}`}>
              {s === "all" ? "All" : STATUS_META[s]?.label}
            </button>
          ))}
        </div>

        {requests.length > 0 && (
          <div className="mt-6 rounded-2xl bg-indigo-50 border border-indigo-200 p-5">
            <div className="text-xs uppercase tracking-widest text-indigo-900 font-semibold">Pending Resolver Requests</div>
            <div className="mt-3 space-y-2">
              {requests.map((r) => (
                <div key={r.id} className="rounded-xl bg-white border border-indigo-100 p-3 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-gray-900">{r.user_name} <span className="text-xs text-gray-500 font-normal">· {r.user_mobile}</span></div>
                    <div className="text-xs text-gray-600">Areas: <b>{(r.service_areas || []).join(", ") || "—"}</b></div>
                    {r.reason && <div className="text-xs text-gray-600 mt-1 whitespace-pre-line">“{r.reason}”</div>}
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`approve-${r.id}`} onClick={() => decideRequest(r.id, "approve")}
                      className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">Approve</button>
                    <button data-testid={`reject-${r.id}`} onClick={() => decideRequest(r.id, "reject")}
                      className="px-3 py-1.5 rounded-full bg-white border border-gray-200 text-gray-700 text-xs font-semibold">Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-2xl bg-white border border-gray-100 premium-shadow">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3">Issue</th>
                  <th className="px-4 py-3">Village</th>
                  <th className="px-4 py-3">Affected</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Rep</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((it) => {
                  const meta = CATEGORY_META[it.category] || CATEGORY_META.other;
                  const sm = STATUS_META[it.status] || STATUS_META.reported;
                  return (
                    <tr key={it.id} className="border-t border-gray-100">
                      <td className="px-4 py-3">
                        <button onClick={() => nav(`/community/${it.id}`)} className="font-semibold text-gray-900 hover:underline text-left">
                          <span className="mr-1">{meta.icon}</span>{it.title}
                        </button>
                        <div className="text-xs text-gray-500">{new Date(it.created_at).toLocaleDateString()}</div>
                      </td>
                      <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-gray-700"><MapPin className="h-3 w-3" />{it.village}</span></td>
                      <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-gray-700"><Users className="h-3 w-3" />{it.affected_count || 1}</span></td>
                      <td className="px-4 py-3">
                        <select value={it.status} onChange={(e) => changeStatus(it.id, e.target.value)}
                          className={`rounded-full border px-2 py-1 text-xs ${sm.color}`}>
                          <option value="reported">Reported</option>
                          <option value="under_review">Under Review</option>
                          <option value="in_progress">Action in Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <select value={it.assigned_rep_id || ""} onChange={(e) => assignRep(it.id, e.target.value)}
                          className="rounded-xl border border-gray-200 px-2 py-1 text-xs">
                          <option value="">— unassigned —</option>
                          {reps.map((r) => <option key={r.id} value={r.id}>{r.full_name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => nav(`/community/${it.id}`)} className="text-xs text-emerald-700 font-semibold hover:underline">View</button>
                      </td>
                    </tr>
                  );
                })}
                {issues.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">No issues match the filter.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 text-xs text-gray-500">
          Tip: assign a GTF Community Representative to each village to speed up follow-up.
          Use <code className="bg-gray-100 px-1 rounded">POST /api/admin/users/{"{user_id}"}/flags</code> with <code>is_rep=true</code> and <code>rep_areas=Village1,Village2</code> to grant.
        </div>
      </div>
    </AppShell>
  );
}
